import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import * as THREE from 'three';

import { FikoAktiviteGravur } from '../FikoAktiviteGravur';
import { FikoVektor3D } from '../../../../FikoVektor3d';
import { FikoSecililer } from '../../../../FikoSecililer';

/**
 * DialogFikoGravurComponent - Gravür İşleme Dialog Bileşeni
 * Compact SVG-based UI matching Profil style
 *
 * 4 farklı gravür tipi:
 * - Çizgi Gravür: Sabit derinlikte çizgi kazıma
 * - V-Gravür: V-kesici ile değişken derinlik (VCarve)
 * - Dolgu Gravür: Alan doldurma (fill engraving)
 * - Nokta Gravür: Stipple/dot engraving
 */
@Component({
    selector: 'dialog-fiko-gravur',
    standalone: true,
    imports: [CommonModule, FormsModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="cam-dialog" *ngIf="aktivite">
            <div class="neon-border"></div>

            <!-- Header -->
            <div class="d-header">
                <svg viewBox="0 0 18 18" class="h-icon">
                    <defs>
                        <linearGradient id="gravurGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stop-color="#0f8"/>
                            <stop offset="100%" stop-color="#0ff"/>
                        </linearGradient>
                    </defs>
                    <path d="M2 16L9 2L16 16H2Z" fill="none" stroke="url(#gravurGrad)" stroke-width="1.5"/>
                    <line x1="5" y1="12" x2="13" y2="12" stroke="url(#gravurGrad)" stroke-width="1"/>
                    <line x1="7" y1="8" x2="11" y2="8" stroke="url(#gravurGrad)" stroke-width="1"/>
                </svg>
                <span class="h-title">GRAVÜR</span>
                <span class="h-badge">{{ aktivite?.profiller?.length || 0 }}</span>
            </div>

            <!-- Tab Header -->
            <div class="tab-header">
                <button class="tab-btn" [class.active]="aktifTab === 0" (click)="setTab(0)">
                    <svg viewBox="0 0 16 16"><path d="M2 3h12M2 8h12M2 13h8" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>
                    <span>Gravür</span>
                </button>
                <button class="tab-btn" [class.active]="aktifTab === 1" (click)="setTab(1)">
                    <svg viewBox="0 0 16 16"><circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" stroke-width="1.5"/><path d="M8 5v6M5 8h6" stroke="currentColor" stroke-width="1"/></svg>
                    <span>{{ gravurTipiKisa }}</span>
                </button>
                <button class="tab-btn" [class.active]="aktifTab === 2" (click)="setTab(2)">
                    <svg viewBox="0 0 16 16"><rect x="4" y="2" width="8" height="12" rx="1" fill="none" stroke="currentColor" stroke-width="1.5"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/></svg>
                    <span>Takım</span>
                </button>
            </div>

            <!-- Tab 0: Gravür Tipi & Profiller -->
            <div class="tab-content" *ngIf="aktifTab === 0">
                <!-- Gravür Tipi Butonları -->
                <div class="type-section">
                    <div class="type-buttons">
                        <button class="type-btn" [class.active]="aktivite.gravurTipi === 'CizgiGravur'" (click)="setGravurTipi('CizgiGravur')">
                            <svg viewBox="0 0 32 24">
                                <defs><linearGradient id="cizgiGrad" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#0f8"/><stop offset="100%" stop-color="#0ff"/></linearGradient></defs>
                                <line x1="4" y1="18" x2="28" y2="18" stroke="#334" stroke-width="8"/>
                                <line x1="4" y1="18" x2="28" y2="18" stroke="url(#cizgiGrad)" stroke-width="2"/>
                            </svg>
                            <span>Çizgi</span>
                        </button>
                        <button class="type-btn" [class.active]="aktivite.gravurTipi === 'VGravur'" (click)="setGravurTipi('VGravur')">
                            <svg viewBox="0 0 32 24">
                                <defs><linearGradient id="vGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#f80"/><stop offset="100%" stop-color="#fa0"/></linearGradient></defs>
                                <polygon points="6,6 26,6 16,22" fill="#223" stroke="url(#vGrad)" stroke-width="1.5"/>
                                <line x1="16" y1="6" x2="16" y2="22" stroke="url(#vGrad)" stroke-width="1" stroke-dasharray="2,1"/>
                            </svg>
                            <span>V-Carve</span>
                        </button>
                        <button class="type-btn" [class.active]="aktivite.gravurTipi === 'DolguGravur'" (click)="setGravurTipi('DolguGravur')">
                            <svg viewBox="0 0 32 24">
                                <defs>
                                    <linearGradient id="dolguGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#08f"/><stop offset="100%" stop-color="#0af"/></linearGradient>
                                    <pattern id="hatchPattern" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                                        <line x1="0" y1="0" x2="0" y2="4" stroke="#08f" stroke-width="1"/>
                                    </pattern>
                                </defs>
                                <rect x="4" y="4" width="24" height="16" rx="2" fill="url(#hatchPattern)" stroke="url(#dolguGrad)" stroke-width="1.5"/>
                            </svg>
                            <span>Dolgu</span>
                        </button>
                        <button class="type-btn" [class.active]="aktivite.gravurTipi === 'NoktaGravur'" (click)="setGravurTipi('NoktaGravur')">
                            <svg viewBox="0 0 32 24">
                                <defs><linearGradient id="noktaGrad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#f08"/><stop offset="100%" stop-color="#f0a"/></linearGradient></defs>
                                <circle cx="8" cy="8" r="2" fill="url(#noktaGrad)"/>
                                <circle cx="16" cy="8" r="2" fill="url(#noktaGrad)"/>
                                <circle cx="24" cy="8" r="2" fill="url(#noktaGrad)"/>
                                <circle cx="12" cy="16" r="2" fill="url(#noktaGrad)"/>
                                <circle cx="20" cy="16" r="2" fill="url(#noktaGrad)"/>
                            </svg>
                            <span>Nokta</span>
                        </button>
                    </div>
                </div>

                <!-- Ana SVG Görsel -->
                <svg viewBox="0 0 280 130" class="main-svg">
                    <defs>
                        <linearGradient id="parcaGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stop-color="#445"/>
                            <stop offset="100%" stop-color="#223"/>
                        </linearGradient>
                        <linearGradient id="gravurDerinlikGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stop-color="#0f8"/>
                            <stop offset="100%" stop-color="#064"/>
                        </linearGradient>
                    </defs>

                    <!-- Parça -->
                    <rect x="20" y="50" width="240" height="60" fill="url(#parcaGrad)" stroke="#556" stroke-width="1"/>

                    <!-- Z Seviyeleri -->
                    <line x1="15" y1="20" x2="265" y2="20" stroke="#0f84" stroke-width="0.5" stroke-dasharray="4,2"/>
                    <text x="10" y="18" class="z-label" text-anchor="end">Güvenli Z</text>

                    <line x1="15" y1="50" x2="265" y2="50" stroke="#0ff4" stroke-width="0.5" stroke-dasharray="4,2"/>
                    <text x="10" y="48" class="z-label" text-anchor="end">Üst Z</text>

                    <!-- Gravür Derinlik Gösterimi -->
                    <rect x="60" y="50" width="160" [attr.height]="gravurDerinlikPx" fill="url(#gravurDerinlikGrad)" opacity="0.6"/>

                    <!-- Gravür Tipi Görseli -->
                    <g *ngIf="aktivite.gravurTipi === 'CizgiGravur'">
                        <path d="M60 55 Q100 60, 140 55 T220 55" fill="none" stroke="#0f8" stroke-width="2"/>
                    </g>
                    <g *ngIf="aktivite.gravurTipi === 'VGravur'">
                        <path d="M80 50 L100 65 L120 50 M140 50 L160 70 L180 50" fill="none" stroke="#f80" stroke-width="1.5"/>
                    </g>
                    <g *ngIf="aktivite.gravurTipi === 'DolguGravur'">
                        <path d="M70 55 H210 M70 62 H210 M70 69 H210 M70 76 H210" stroke="#08f" stroke-width="1" opacity="0.7"/>
                    </g>
                    <g *ngIf="aktivite.gravurTipi === 'NoktaGravur'">
                        <circle cx="80" cy="58" r="3" fill="#f08"/>
                        <circle cx="110" cy="58" r="3" fill="#f08"/>
                        <circle cx="140" cy="58" r="3" fill="#f08"/>
                        <circle cx="170" cy="58" r="3" fill="#f08"/>
                        <circle cx="200" cy="58" r="3" fill="#f08"/>
                    </g>

                    <!-- Takım -->
                    <g transform="translate(140, 10)">
                        <rect x="-3" y="0" width="6" height="30" fill="#888"/>
                        <polygon points="-4,30 4,30 0,42" fill="#cc0" stroke="#aa0"/>
                    </g>

                    <!-- Profil Sayısı -->
                    <text x="140" y="125" class="info-text" text-anchor="middle">{{ aktivite.profiller?.length || 0 }} profil seçili</text>
                </svg>

                <!-- Profil Seçimi -->
                <div class="param-grid">
                    <div class="p-item select-btn" [class.selecting]="secimModu === 'profilSec'" (click)="secProfil()">
                        <span class="p-label">Profil</span>
                        <span class="p-value">{{ secimModu === 'profilSec' ? 'Seçim...' : 'SEÇ' }}</span>
                    </div>
                    <div class="p-item" (click)="temizleProfiller()">
                        <span class="p-label">Temizle</span>
                        <span class="p-value">✕</span>
                    </div>
                </div>
                <div class="select-hint" *ngIf="secimModu === 'profilSec'">
                    3D sahnede gravür yapılacak kenarları seçin. ESC: Bitir
                </div>

                <!-- Z Parametreleri -->
                <div class="param-grid">
                    <div class="p-item" (click)="editValue('ustZ', 'Üst Z', 'mm')">
                        <span class="p-label">Üst Z</span>
                        <span class="p-value">{{ aktivite.ustZ?.toFixed(2) || '0.00' }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('guvenliZ', 'Güvenli Z', 'mm')">
                        <span class="p-label">Güvenli Z</span>
                        <span class="p-value">{{ aktivite.guvenliZ?.toFixed(2) || '5.00' }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('gravurDerinlik', 'Gravür Derinliği', 'mm')">
                        <span class="p-label">Derinlik</span>
                        <span class="p-value">{{ aktivite.gravurDerinlik?.toFixed(2) || '0.30' }}</span>
                    </div>
                    <div class="p-item action" (click)="hesaplaVeKaydet()">
                        <span class="p-label">İşlem</span>
                        <span class="p-value act">HESAPLA</span>
                    </div>
                </div>
            </div>

            <!-- Tab 1: Tip Parametreleri -->
            <div class="tab-content" *ngIf="aktifTab === 1">
                <!-- Çizgi Gravür -->
                <div *ngIf="aktivite.gravurTipi === 'CizgiGravur'" class="tip-content">
                    <svg viewBox="0 0 280 100" class="tip-svg">
                        <defs>
                            <linearGradient id="cizgiTipGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                                <stop offset="0%" stop-color="#0f8"/><stop offset="100%" stop-color="#0ff"/>
                            </linearGradient>
                        </defs>
                        <rect x="20" y="50" width="240" height="40" fill="#334"/>
                        <!-- Çizgi kesiti -->
                        <rect x="80" y="50" [attr.width]="aktivite.cizgiGenislik * 10 || 20" [attr.height]="aktivite.cizgiDerinlik * 20 || 10" fill="url(#cizgiTipGrad)" opacity="0.7"/>
                        <text x="90" y="45" class="dim-text">W: {{ aktivite.cizgiGenislik || 2 }}mm</text>
                        <text x="180" y="70" class="dim-text">D: {{ aktivite.cizgiDerinlik || 0.5 }}mm</text>
                        <!-- Offset gösterimi -->
                        <line x1="90" y1="30" x2="90" y2="50" stroke="#0f8" stroke-width="0.5" stroke-dasharray="2,1"/>
                        <text x="90" y="25" class="info-text" text-anchor="middle">{{ aktivite.cizgiOffset || 'Merkez' }}</text>
                    </svg>
                    <div class="param-grid">
                        <div class="p-item" (click)="editValue('cizgiDerinlik', 'Çizgi Derinliği', 'mm')">
                            <span class="p-label">Derinlik</span>
                            <span class="p-value">{{ aktivite.cizgiDerinlik?.toFixed(2) || '0.50' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('cizgiGenislik', 'Çizgi Genişliği', 'mm')">
                            <span class="p-label">Genişlik</span>
                            <span class="p-value">{{ aktivite.cizgiGenislik?.toFixed(2) || '2.00' }}</span>
                        </div>
                        <div class="p-item" (click)="cycleOffset()">
                            <span class="p-label">Offset</span>
                            <span class="p-value">{{ aktivite.cizgiOffset || 'Merkez' }}</span>
                        </div>
                    </div>
                </div>

                <!-- V-Gravür -->
                <div *ngIf="aktivite.gravurTipi === 'VGravur'" class="tip-content">
                    <svg viewBox="0 0 280 100" class="tip-svg">
                        <defs>
                            <linearGradient id="vTipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stop-color="#f80"/><stop offset="100%" stop-color="#fa0"/>
                            </linearGradient>
                        </defs>
                        <rect x="20" y="30" width="240" height="60" fill="#334"/>
                        <!-- V kesiti -->
                        <polygon [attr.points]="vKesitiPoints" fill="url(#vTipGrad)" opacity="0.7"/>
                        <!-- Açı gösterimi -->
                        <path [attr.d]="vAciPath" fill="none" stroke="#f80" stroke-width="0.5"/>
                        <text x="140" y="20" class="dim-text" text-anchor="middle">{{ aktivite.vAci || 60 }}°</text>
                        <text x="200" y="60" class="dim-text">Max: {{ aktivite.vMaxDerinlik || 5 }}mm</text>
                    </svg>
                    <div class="param-grid">
                        <div class="p-item" (click)="cycleVAci()">
                            <span class="p-label">V Açısı</span>
                            <span class="p-value">{{ aktivite.vAci || 60 }}°</span>
                        </div>
                        <div class="p-item" (click)="toggleOtomatikDerinlik()">
                            <span class="p-label">Oto Derinlik</span>
                            <span class="p-value" [class.active]="aktivite.vOtomatikDerinlik">{{ aktivite.vOtomatikDerinlik ? 'AÇIK' : 'KAPALI' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('vMaxDerinlik', 'Max Derinlik', 'mm')" *ngIf="aktivite.vOtomatikDerinlik">
                            <span class="p-label">Max Derinlik</span>
                            <span class="p-value">{{ aktivite.vMaxDerinlik?.toFixed(2) || '5.00' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('vMinDerinlik', 'Min Derinlik', 'mm')" *ngIf="aktivite.vOtomatikDerinlik">
                            <span class="p-label">Min Derinlik</span>
                            <span class="p-value">{{ aktivite.vMinDerinlik?.toFixed(2) || '0.10' }}</span>
                        </div>
                    </div>
                    <div class="info-bar">
                        <span class="info-icon">ℹ</span>
                        <span>Derinlik = Genişlik / (2 × tan(Açı/2))</span>
                    </div>
                </div>

                <!-- Dolgu Gravür -->
                <div *ngIf="aktivite.gravurTipi === 'DolguGravur'" class="tip-content">
                    <svg viewBox="0 0 280 100" class="tip-svg">
                        <defs>
                            <linearGradient id="dolguTipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stop-color="#08f"/><stop offset="100%" stop-color="#0af"/>
                            </linearGradient>
                            <pattern id="offsetPat" patternUnits="userSpaceOnUse" width="8" height="8">
                                <path d="M0 4 Q4 0 8 4 Q4 8 0 4" fill="none" stroke="#08f" stroke-width="0.5"/>
                            </pattern>
                            <pattern id="rasterPat" patternUnits="userSpaceOnUse" width="6" height="6" [attr.patternTransform]="'rotate(' + (aktivite.dolguAci || 0) + ')'">
                                <line x1="0" y1="3" x2="6" y2="3" stroke="#08f" stroke-width="1"/>
                            </pattern>
                            <pattern id="crossPat" patternUnits="userSpaceOnUse" width="6" height="6">
                                <line x1="0" y1="0" x2="6" y2="6" stroke="#08f" stroke-width="0.5"/>
                                <line x1="6" y1="0" x2="0" y2="6" stroke="#08f" stroke-width="0.5"/>
                            </pattern>
                        </defs>
                        <rect x="40" y="20" width="200" height="60" [attr.fill]="dolguPatternId" stroke="url(#dolguTipGrad)" stroke-width="2"/>
                        <text x="140" y="90" class="info-text" text-anchor="middle">Desen: {{ aktivite.dolguDeseni || 'Offset' }}</text>
                    </svg>
                    <div class="param-grid">
                        <div class="p-item" (click)="cycleDolguDeseni()">
                            <span class="p-label">Desen</span>
                            <span class="p-value">{{ aktivite.dolguDeseni || 'Offset' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('dolguAdim', 'Dolgu Adımı', 'mm')">
                            <span class="p-label">Adım</span>
                            <span class="p-value">{{ aktivite.dolguAdim?.toFixed(2) || '1.00' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('dolguAci', 'Raster Açısı', '°')" *ngIf="aktivite.dolguDeseni === 'Raster' || aktivite.dolguDeseni === 'CrossHatch'">
                            <span class="p-label">Açı</span>
                            <span class="p-value">{{ aktivite.dolguAci || 0 }}°</span>
                        </div>
                        <div class="p-item" (click)="editValue('dolguOverlap', 'Overlap', '%')">
                            <span class="p-label">Overlap</span>
                            <span class="p-value">{{ aktivite.dolguOverlap || 10 }}%</span>
                        </div>
                        <div class="p-item" (click)="toggleDolguCevre()">
                            <span class="p-label">Çevre Geçişi</span>
                            <span class="p-value" [class.active]="aktivite.dolguCevre">{{ aktivite.dolguCevre ? 'AÇIK' : 'KAPALI' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('dolguCevreSayisi', 'Çevre Sayısı', '')" *ngIf="aktivite.dolguCevre">
                            <span class="p-label">Çevre Sayısı</span>
                            <span class="p-value">{{ aktivite.dolguCevreSayisi || 1 }}</span>
                        </div>
                    </div>
                </div>

                <!-- Nokta Gravür -->
                <div *ngIf="aktivite.gravurTipi === 'NoktaGravur'" class="tip-content">
                    <svg viewBox="0 0 280 100" class="tip-svg">
                        <defs>
                            <linearGradient id="noktaTipGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                <stop offset="0%" stop-color="#f08"/><stop offset="100%" stop-color="#f0a"/>
                            </linearGradient>
                        </defs>
                        <rect x="40" y="20" width="200" height="60" fill="#223" stroke="#445"/>
                        <!-- Nokta deseni -->
                        <g *ngIf="aktivite.noktaDesen === 'Grid' || !aktivite.noktaDesen">
                            <circle *ngFor="let p of gridPoints" [attr.cx]="p.x" [attr.cy]="p.y" r="3" fill="url(#noktaTipGrad)"/>
                        </g>
                        <g *ngIf="aktivite.noktaDesen === 'Hex'">
                            <circle *ngFor="let p of hexPoints" [attr.cx]="p.x" [attr.cy]="p.y" r="3" fill="url(#noktaTipGrad)"/>
                        </g>
                        <g *ngIf="aktivite.noktaDesen === 'Random'">
                            <circle *ngFor="let p of randomPoints" [attr.cx]="p.x" [attr.cy]="p.y" r="3" fill="url(#noktaTipGrad)"/>
                        </g>
                    </svg>
                    <div class="param-grid">
                        <div class="p-item" (click)="cycleNoktaDesen()">
                            <span class="p-label">Desen</span>
                            <span class="p-value">{{ aktivite.noktaDesen || 'Grid' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('noktaAralik', 'Nokta Aralığı', 'mm')">
                            <span class="p-label">Aralık</span>
                            <span class="p-value">{{ aktivite.noktaAralik?.toFixed(1) || '2.0' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('noktaDerinlik', 'Nokta Derinliği', 'mm')">
                            <span class="p-label">Derinlik</span>
                            <span class="p-value">{{ aktivite.noktaDerinlik?.toFixed(2) || '0.20' }}</span>
                        </div>
                        <div class="p-item" (click)="editValue('noktaBekleme', 'Nokta Bekleme', 'sn')">
                            <span class="p-label">Bekleme</span>
                            <span class="p-value">{{ aktivite.noktaBekleme?.toFixed(2) || '0.00' }}s</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tab 2: Takım & Hız -->
            <div class="tab-content" *ngIf="aktifTab === 2">
                <svg viewBox="0 0 280 120" class="tip-svg">
                    <defs>
                        <linearGradient id="takimGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stop-color="#888"/>
                            <stop offset="100%" stop-color="#555"/>
                        </linearGradient>
                        <linearGradient id="takimUcGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                            <stop offset="0%" stop-color="#cc0"/>
                            <stop offset="100%" stop-color="#990"/>
                        </linearGradient>
                    </defs>
                    <!-- Takım gövde -->
                    <rect x="130" y="10" width="20" height="60" fill="url(#takimGrad)" rx="2"/>
                    <!-- Takım ucu (tipe göre) -->
                    <polygon *ngIf="aktivite.takim?.takimTipi === 'VKesici'" points="130,70 150,70 140,100" fill="url(#takimUcGrad)"/>
                    <ellipse *ngIf="aktivite.takim?.takimTipi === 'BallNose'" cx="140" cy="80" rx="10" ry="15" fill="url(#takimUcGrad)"/>
                    <rect *ngIf="aktivite.takim?.takimTipi === 'ParmakFreze' || aktivite.takim?.takimTipi === 'KarbonUc'" x="130" y="70" width="20" height="25" fill="url(#takimUcGrad)" rx="2"/>
                    <polygon *ngIf="aktivite.takim?.takimTipi === 'Elmas'" points="135,70 145,70 140,95" fill="url(#takimUcGrad)"/>
                    <!-- Çap gösterimi -->
                    <line x1="125" y1="75" x2="155" y2="75" stroke="#0f8" stroke-width="0.5"/>
                    <text x="160" y="78" class="dim-text">Ø{{ aktivite.takim?.cap || 6 }}</text>
                    <!-- Takım adı -->
                    <text x="140" y="115" class="info-text" text-anchor="middle">{{ aktivite.takim?.ad || 'T1' }} - {{ aktivite.takim?.takimTipi || 'VKesici' }}</text>
                </svg>

                <div class="param-grid">
                    <div class="p-item" (click)="editTakimAd()">
                        <span class="p-label">Takım Adı</span>
                        <span class="p-value">{{ aktivite.takim?.ad || 'T1' }}</span>
                    </div>
                    <div class="p-item" (click)="cycleTakimTipi()">
                        <span class="p-label">Takım Tipi</span>
                        <span class="p-value">{{ aktivite.takim?.takimTipi || 'VKesici' }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('takim.cap', 'Takım Çapı', 'mm')">
                        <span class="p-label">Çap</span>
                        <span class="p-value">{{ aktivite.takim?.cap?.toFixed(1) || '6.0' }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('devir', 'Spindle Devri', 'rpm')">
                        <span class="p-label">Devir</span>
                        <span class="p-value">{{ aktivite.devir || 12000 }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('ilerlemeGravur', 'Gravür İlerleme', 'mm/dk')">
                        <span class="p-label">Gravür Hız</span>
                        <span class="p-value">{{ aktivite.ilerlemeGravur || 500 }}</span>
                    </div>
                    <div class="p-item" (click)="editValue('ilerlemeDalma', 'Dalma İlerleme', 'mm/dk')">
                        <span class="p-label">Dalma Hız</span>
                        <span class="p-value">{{ aktivite.ilerlemeDalma || 100 }}</span>
                    </div>
                </div>

                <!-- Giriş/Çıkış -->
                <div class="sub-section">
                    <div class="sub-title">Giriş/Çıkış</div>
                    <div class="param-grid">
                        <div class="p-item" (click)="cycleGirisYontemi()">
                            <span class="p-label">Giriş</span>
                            <span class="p-value">{{ aktivite.girisYontemi || 'Dikey' }}</span>
                        </div>
                        <div class="p-item" (click)="cycleCikisYontemi()">
                            <span class="p-label">Çıkış</span>
                            <span class="p-value">{{ aktivite.cikisYontemi || 'Dikey' }}</span>
                        </div>
                    </div>
                </div>

                <!-- Özet -->
                <div class="summary-bar">
                    <div class="sum-item">
                        <span class="s-label">Profil</span>
                        <span class="s-value">{{ aktivite.profiller?.length || 0 }}</span>
                    </div>
                    <div class="sum-item">
                        <span class="s-label">Mesafe</span>
                        <span class="s-value">{{ (aktivite.toplamMesafe || 0).toFixed(0) }}mm</span>
                    </div>
                    <div class="sum-item">
                        <span class="s-label">Süre</span>
                        <span class="s-value">{{ formatSure(aktivite.hesaplananSure || 0) }}</span>
                    </div>
                </div>
            </div>

            <!-- Edit Popup -->
            <div class="edit-popup" *ngIf="editingField">
                <div class="ep-header">{{ editLabel }}</div>
                <div class="ep-input">
                    <input type="number" [(ngModel)]="editTempValue" (keyup.enter)="applyEdit()" (keyup.escape)="cancelEdit()" #editInput/>
                    <span class="ep-unit">{{ editUnit }}</span>
                </div>
                <div class="ep-actions">
                    <button class="ep-btn cancel" (click)="cancelEdit()">✕</button>
                    <button class="ep-btn apply" (click)="applyEdit()">✓</button>
                </div>
            </div>
        </div>
    `,
    styles: [`
        .cam-dialog {
            position: relative;
            width: 260px;
            background: linear-gradient(135deg, #0a1520 0%, #0d1825 50%, #0a1520 100%);
            border-radius: 8px;
            overflow: hidden;
            font-family: 'Segoe UI', system-ui, sans-serif;
            box-shadow: 0 8px 32px rgba(0,0,0,0.5);
        }

        .neon-border {
            position: absolute;
            inset: 0;
            border-radius: 8px;
            padding: 1px;
            background: linear-gradient(135deg, #0f8, #0ff, #08f, #0f8);
            -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
            -webkit-mask-composite: xor;
            mask-composite: exclude;
            pointer-events: none;
            opacity: 0.6;
        }

        .d-header {
            display: flex;
            align-items: center;
            gap: 5px;
            padding: 5px 8px;
            background: linear-gradient(90deg, rgba(0,255,136,0.1) 0%, transparent 100%);
            border-bottom: 1px solid #1a3040;
        }

        .h-icon { width: 15px; height: 15px; }
        .h-title { color: #0f8; font-size: 10px; font-weight: 600; letter-spacing: 1px; flex: 1; }
        .h-badge {
            background: linear-gradient(135deg, #0f8, #0a6);
            color: #000;
            font-size: 8px;
            font-weight: 700;
            padding: 1px 4px;
            border-radius: 6px;
        }

        .tab-header {
            display: flex;
            gap: 2px;
            padding: 4px 5px;
            background: rgba(0,0,0,0.3);
        }

        .tab-btn {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1px;
            padding: 4px 3px;
            background: transparent;
            border: 1px solid transparent;
            border-radius: 4px;
            color: #556;
            cursor: pointer;
            transition: all 0.2s;
        }

        .tab-btn svg { width: 12px; height: 12px; }
        .tab-btn span { font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; }

        .tab-btn:hover { color: #8af; background: rgba(136,170,255,0.1); }
        .tab-btn.active {
            color: #0f8;
            background: rgba(0,255,136,0.1);
            border-color: rgba(0,255,136,0.3);
        }

        .tab-content { padding: 5px; }

        /* Type Section */
        .type-section { margin-bottom: 5px; }

        .type-buttons {
            display: flex;
            gap: 3px;
        }

        .type-btn {
            flex: 1;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 2px;
            padding: 4px 1px;
            background: rgba(0,0,0,0.3);
            border: 1px solid #1a3040;
            border-radius: 4px;
            color: #668;
            cursor: pointer;
            transition: all 0.2s;
        }

        .type-btn svg { width: 26px; height: 20px; }
        .type-btn span { font-size: 7px; text-transform: uppercase; }

        .type-btn:hover { border-color: #0f8; color: #adf; }
        .type-btn.active {
            background: rgba(0,255,136,0.15);
            border-color: #0f8;
            color: #0f8;
            box-shadow: 0 0 10px rgba(0,255,136,0.2);
        }

        .main-svg, .tip-svg {
            width: 100%;
            background: rgba(0,0,0,0.2);
            border-radius: 4px;
            margin-bottom: 5px;
        }

        .z-label { fill: #556; font-size: 6px; }
        .dim-text { fill: #8af; font-size: 7px; }
        .info-text { fill: #668; font-size: 8px; }

        /* Param Grid */
        .param-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 3px;
        }

        .p-item {
            display: flex;
            flex-direction: column;
            gap: 1px;
            padding: 4px 6px;
            background: rgba(0,0,0,0.3);
            border: 1px solid #1a3040;
            border-radius: 4px;
            cursor: pointer;
            transition: all 0.15s;
        }

        .p-item:hover {
            border-color: #0f8;
            background: rgba(0,255,136,0.05);
        }

        .p-label { color: #556; font-size: 7px; text-transform: uppercase; letter-spacing: 0.5px; }
        .p-value { color: #adf; font-size: 10px; font-weight: 600; }
        .p-value.active { color: #0f8; }
        .p-value.act { color: #0f8; }

        .p-item.action { background: rgba(0,255,136,0.1); border-color: rgba(0,255,136,0.3); }
        .p-item.action:hover { background: rgba(0,255,136,0.2); }
        .p-item.select-btn { background: rgba(0,100,80,0.2); border-color: rgba(0,255,136,0.3); cursor: pointer; }
        .p-item.select-btn:hover { background: rgba(0,255,136,0.15); }
        .p-item.select-btn.selecting {
            animation: pulse-select 1.5s infinite;
            border-color: #0f8;
            box-shadow: 0 0 10px rgba(0,255,136,0.5);
        }
        @keyframes pulse-select {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
        }
        .select-hint {
            margin: 5px 6px;
            padding: 4px 7px;
            background: rgba(0,255,136,0.1);
            border: 1px solid rgba(0,255,136,0.3);
            border-radius: 4px;
            font-size: 8px;
            color: #0f8;
            text-align: center;
        }

        /* Tip Content */
        .tip-content { }

        .info-bar {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 4px 7px;
            background: rgba(0,136,255,0.1);
            border: 1px solid rgba(0,136,255,0.2);
            border-radius: 4px;
            margin-top: 5px;
        }

        .info-icon { color: #08f; font-size: 11px; }
        .info-bar span { color: #8af; font-size: 8px; }

        /* Sub Section */
        .sub-section {
            margin-top: 7px;
            padding-top: 5px;
            border-top: 1px solid #1a3040;
        }

        .sub-title {
            color: #556;
            font-size: 7px;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 4px;
        }

        /* Summary Bar */
        .summary-bar {
            display: flex;
            gap: 3px;
            margin-top: 7px;
            padding: 5px;
            background: rgba(0,255,136,0.05);
            border: 1px solid rgba(0,255,136,0.15);
            border-radius: 4px;
        }

        .sum-item { flex: 1; text-align: center; }
        .s-label { display: block; color: #556; font-size: 6px; text-transform: uppercase; }
        .s-value { display: block; color: #0f8; font-size: 9px; font-weight: 600; }

        /* Edit Popup */
        .edit-popup {
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: linear-gradient(135deg, #0d1825, #0a1520);
            border: 1px solid #0f8;
            border-radius: 8px;
            padding: 10px;
            min-width: 160px;
            box-shadow: 0 8px 32px rgba(0,255,136,0.2);
            z-index: 100;
        }

        .ep-header {
            color: #0f8;
            font-size: 9px;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 6px;
            text-align: center;
        }

        .ep-input {
            display: flex;
            align-items: center;
            gap: 4px;
            background: rgba(0,0,0,0.4);
            border: 1px solid #1a3040;
            border-radius: 4px;
            padding: 3px 6px;
        }

        .ep-input input {
            flex: 1;
            background: transparent;
            border: none;
            color: #fff;
            font-size: 13px;
            font-weight: 600;
            text-align: center;
            outline: none;
            width: 70px;
        }

        .ep-unit { color: #556; font-size: 9px; }

        .ep-actions {
            display: flex;
            gap: 4px;
            margin-top: 7px;
        }

        .ep-btn {
            flex: 1;
            padding: 5px;
            border: none;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.15s;
        }

        .ep-btn.cancel { background: #311; color: #f66; }
        .ep-btn.cancel:hover { background: #422; }
        .ep-btn.apply { background: #131; color: #0f8; }
        .ep-btn.apply:hover { background: #1a4; }
    `]
})
export class DialogFikoGravurComponent implements OnInit, OnDestroy {
    @Input() aktivite: FikoAktiviteGravur;
    @Input() anaGosterim: any; // FikoThreeJsComponent referansı
    @Input() pencere: any;
    @Output() onKaydet = new EventEmitter<FikoAktiviteGravur>();
    @Output() onIptal = new EventEmitter<void>();

    aktifTab = 0;

    // Edit popup
    editingField: string = null;
    editLabel: string = '';
    editUnit: string = '';
    editTempValue: number = 0;

    // 3D Seçim State
    secimModu: 'yok' | 'profilSec' = 'yok';
    private fikoSecililer: FikoSecililer | null = null;
    private secimSubscriptions: Subscription[] = [];
    private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

    // Grid/Hex/Random points for nokta gravür preview
    gridPoints = [
        {x: 60, y: 30}, {x: 90, y: 30}, {x: 120, y: 30}, {x: 150, y: 30}, {x: 180, y: 30}, {x: 210, y: 30},
        {x: 60, y: 50}, {x: 90, y: 50}, {x: 120, y: 50}, {x: 150, y: 50}, {x: 180, y: 50}, {x: 210, y: 50},
        {x: 60, y: 70}, {x: 90, y: 70}, {x: 120, y: 70}, {x: 150, y: 70}, {x: 180, y: 70}, {x: 210, y: 70}
    ];

    hexPoints = [
        {x: 60, y: 30}, {x: 100, y: 30}, {x: 140, y: 30}, {x: 180, y: 30}, {x: 220, y: 30},
        {x: 80, y: 50}, {x: 120, y: 50}, {x: 160, y: 50}, {x: 200, y: 50},
        {x: 60, y: 70}, {x: 100, y: 70}, {x: 140, y: 70}, {x: 180, y: 70}, {x: 220, y: 70}
    ];

    randomPoints = [
        {x: 55, y: 35}, {x: 95, y: 28}, {x: 130, y: 42}, {x: 165, y: 33}, {x: 200, y: 45}, {x: 225, y: 30},
        {x: 70, y: 55}, {x: 110, y: 62}, {x: 145, y: 48}, {x: 185, y: 58}, {x: 215, y: 52},
        {x: 58, y: 72}, {x: 88, y: 68}, {x: 128, y: 75}, {x: 170, y: 65}, {x: 210, y: 72}
    ];

    constructor(private cdr: ChangeDetectorRef) {}

    ngOnInit(): void {
        if (!this.aktivite) {
            this.aktivite = new FikoAktiviteGravur();
        }
    }

    ngOnDestroy(): void {
        this.secimModunuKapat();
    }

    // ═══════════════════════════════════════════════════════════════════
    // 3D SEÇİM SİSTEMİ - FikoSecililer Entegrasyonu
    // ═══════════════════════════════════════════════════════════════════

    /**
     * Profil seçimi başlat - Edge/çizgi seçimi
     */
    secProfil(): void {
        if (this.secimModu === 'profilSec') {
            // Seçim modundaysa kapat
            this.secimModunuKapat();
            return;
        }
        this.secimModu = 'profilSec';
        this.baslat3DSecim();
        this.cdr.markForCheck();
    }

    /**
     * Profilleri temizle
     */
    temizleProfiller(): void {
        if (this.aktivite) {
            this.aktivite.profiller = [];
            this.cdr.markForCheck();
        }
    }

    /**
     * 3D seçim modunu başlat - FikoSecililer kullanarak
     */
    private baslat3DSecim(): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;
        if (!gosterim?.sahneler?.[0]) {
            console.warn('[Gravür Dialog] Sahne bulunamadı');
            return;
        }

        // FikoSecililer'i al
        this.fikoSecililer = gosterim.sahneler[0].secimler;
        if (!this.fikoSecililer) {
            console.warn('[Gravür Dialog] FikoSecililer bulunamadı');
            return;
        }

        // Önceki subscription'ları temizle
        this.secimSubscriptionTemizle();

        // FikoSecililer'i yapılandır
        this.fikoSecililer.aktif = true;
        this.fikoSecililer.kaliciSecimModu = true;
        this.fikoSecililer.hoverHighlightEnabled = true;
        this.fikoSecililer.temizle();

        // Gravür için edge seçimi (çizgiler/kenarlar)
        this.fikoSecililer.filitreler = ['LineSegments', 'LineSegments2', 'Line2', 'LineGeometry', 'Line', 'Mesh'];

        // Tıklama event'ini dinle
        const tiklandiSub = this.fikoSecililer.tiklandi.subscribe((kesisim: any) => {
            if (kesisim) {
                this.handleSecimKesisim(kesisim);
            }
        });
        this.secimSubscriptions.push(tiklandiSub);

        // Hover event'ini dinle (görsel feedback için)
        const uzerindeSub = this.fikoSecililer.uzerinde.subscribe((kesisim: any) => {
            if (kesisim && gosterim.ciz) {
                gosterim.ciz();
            }
        });
        this.secimSubscriptions.push(uzerindeSub);

        // Keyboard handler (ESC kapatır)
        this.keyDownHandler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                this.secimModunuKapat();
            }
        };
        document.addEventListener('keydown', this.keyDownHandler);

        // Cursor değiştir
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'crosshair';
        }
    }

    /**
     * Seçim kesişimini işle - Profil/edge ekle
     */
    private handleSecimKesisim(kesisim: any): void {
        if (!kesisim || !this.aktivite) return;

        const obj = kesisim.altObje || kesisim.object;
        const point = kesisim.point?.clone() || new THREE.Vector3();

        // Edge vertex'lerini çıkar
        const vertices = this.edgeVerticesFromKesisim(kesisim, obj);

        if (vertices.length < 2) {
            console.warn('[Gravür Dialog] Yetersiz edge vertex sayısı');
            return;
        }

        // FikoVektor3D array formatına çevir (profil)
        const profil = vertices.map(v => new FikoVektor3D(v.x, v.y, v.z));

        // Profil listesine ekle
        if (!this.aktivite.profiller) {
            this.aktivite.profiller = [];
        }
        this.aktivite.profiller.push(profil);

        // Z limitlerini güncelle (ilk seçimde)
        if (this.aktivite.profiller.length === 1) {
            const zValues = vertices.map(v => v.z);
            this.aktivite.ustZ = Math.max(...zValues);
        }

        this.cdr.markForCheck();
    }

    /**
     * Edge vertex'lerini çıkar
     */
    private edgeVerticesFromKesisim(kesisim: any, obj: THREE.Object3D): THREE.Vector3[] {
        const vertices: THREE.Vector3[] = [];

        // FikoSecililer'in highlight geometrisini önce kontrol et
        if (this.fikoSecililer?.sonSecilenHighlightGeometri) {
            const geom = this.fikoSecililer.sonSecilenHighlightGeometri;
            const pos = geom.attributes?.position;
            if (pos && pos.count > 0) {
                const worldMatrix = obj.matrixWorld?.clone() || new THREE.Matrix4();
                for (let i = 0; i < pos.count; i++) {
                    const v = new THREE.Vector3().fromBufferAttribute(pos, i);
                    v.applyMatrix4(worldMatrix);
                    if (!isNaN(v.x) && !isNaN(v.y) && !isNaN(v.z)) {
                        vertices.push(v);
                    }
                }
                if (vertices.length > 0) return vertices;
            }
        }

        // Fallback: Mesh face edge'i
        if (obj instanceof THREE.Mesh && kesisim.faceIndex !== undefined) {
            const geometry = obj.geometry;
            const position = geometry.attributes?.position;
            if (!position) return vertices;

            const index = geometry.index;
            const faceIndex = kesisim.faceIndex;
            const worldMatrix = obj.matrixWorld;

            let i0: number, i1: number, i2: number;
            if (index) {
                i0 = index.getX(faceIndex * 3);
                i1 = index.getX(faceIndex * 3 + 1);
                i2 = index.getX(faceIndex * 3 + 2);
            } else {
                i0 = faceIndex * 3;
                i1 = faceIndex * 3 + 1;
                i2 = faceIndex * 3 + 2;
            }

            const v0 = new THREE.Vector3().fromBufferAttribute(position, i0).applyMatrix4(worldMatrix);
            const v1 = new THREE.Vector3().fromBufferAttribute(position, i1).applyMatrix4(worldMatrix);
            const v2 = new THREE.Vector3().fromBufferAttribute(position, i2).applyMatrix4(worldMatrix);

            // En yakın edge'i bul (kesişim noktasına göre)
            const point = kesisim.point;
            const edges = [
                { v: [v0, v1], dist: this.pointToSegmentDistance(point, v0, v1) },
                { v: [v1, v2], dist: this.pointToSegmentDistance(point, v1, v2) },
                { v: [v2, v0], dist: this.pointToSegmentDistance(point, v2, v0) }
            ];
            edges.sort((a, b) => a.dist - b.dist);
            return edges[0].v;
        }

        // Fallback: Line/LineSegments
        if (obj instanceof THREE.Line || obj instanceof THREE.LineSegments) {
            const geometry = obj.geometry as THREE.BufferGeometry;
            const position = geometry.attributes?.position;
            if (!position) return vertices;

            const worldMatrix = obj.matrixWorld;
            for (let i = 0; i < position.count; i++) {
                const v = new THREE.Vector3().fromBufferAttribute(position, i).applyMatrix4(worldMatrix);
                vertices.push(v);
            }
        }

        return vertices;
    }

    /**
     * Nokta-segment mesafesi hesapla
     */
    private pointToSegmentDistance(p: THREE.Vector3, a: THREE.Vector3, b: THREE.Vector3): number {
        const ab = new THREE.Vector3().subVectors(b, a);
        const ap = new THREE.Vector3().subVectors(p, a);
        const t = Math.max(0, Math.min(1, ap.dot(ab) / ab.dot(ab)));
        const closest = new THREE.Vector3().copy(a).add(ab.multiplyScalar(t));
        return p.distanceTo(closest);
    }

    /**
     * Seçim modunu kapat ve temizle
     */
    private secimModunuKapat(): void {
        const gosterim = this.anaGosterim || this.pencere?.anaGosterim;

        // Keyboard handler'ı kaldır
        if (this.keyDownHandler) {
            document.removeEventListener('keydown', this.keyDownHandler);
            this.keyDownHandler = null;
        }

        // Cursor'u normale döndür
        const canvas = gosterim?.sunucu?.domElement as HTMLCanvasElement;
        if (canvas) {
            canvas.style.cursor = 'default';
        }

        // FikoSecililer'i deaktive et
        if (this.fikoSecililer) {
            this.fikoSecililer.aktif = false;
            this.fikoSecililer.temizle();
            this.fikoSecililer = null;
        }

        // Subscription'ları temizle
        this.secimSubscriptionTemizle();

        this.secimModu = 'yok';
        this.cdr.markForCheck();
    }

    /**
     * Subscription'ları temizle
     */
    private secimSubscriptionTemizle(): void {
        this.secimSubscriptions.forEach(sub => {
            if (sub && !sub.closed) {
                sub.unsubscribe();
            }
        });
        this.secimSubscriptions = [];
    }

    setTab(idx: number): void {
        this.aktifTab = idx;
        this.cdr.markForCheck();
    }

    get gravurTipiKisa(): string {
        switch (this.aktivite?.gravurTipi) {
            case 'CizgiGravur': return 'Çizgi';
            case 'VGravur': return 'V-Carve';
            case 'DolguGravur': return 'Dolgu';
            case 'NoktaGravur': return 'Nokta';
            default: return 'Params';
        }
    }

    get gravurDerinlikPx(): number {
        const d = this.aktivite?.gravurDerinlik || 0.3;
        return Math.min(d * 30, 40);
    }

    get vKesitiPoints(): string {
        const aci = this.aktivite?.vAci || 60;
        const halfAngle = (aci / 2) * Math.PI / 180;
        const depth = 50;
        const halfWidth = depth * Math.tan(halfAngle);
        const cx = 140;
        const topY = 30;
        const bottomY = topY + depth;
        return `${cx - halfWidth},${topY} ${cx + halfWidth},${topY} ${cx},${bottomY}`;
    }

    get vAciPath(): string {
        const aci = this.aktivite?.vAci || 60;
        const halfAngle = (aci / 2) * Math.PI / 180;
        const r = 15;
        const cx = 140;
        const topY = 30;
        const x1 = cx - r * Math.sin(halfAngle);
        const y1 = topY + r * Math.cos(halfAngle);
        const x2 = cx + r * Math.sin(halfAngle);
        return `M${x1},${y1} A${r},${r} 0 0,1 ${x2},${y1}`;
    }

    get dolguPatternId(): string {
        switch (this.aktivite?.dolguDeseni) {
            case 'Raster': return 'url(#rasterPat)';
            case 'CrossHatch': return 'url(#crossPat)';
            case 'Spiral': return 'url(#offsetPat)';
            default: return 'url(#offsetPat)';
        }
    }

    setGravurTipi(tip: 'CizgiGravur' | 'VGravur' | 'DolguGravur' | 'NoktaGravur'): void {
        if (this.aktivite) {
            this.aktivite.gravurTipi = tip;
            // Takım tipini otomatik ayarla
            switch (tip) {
                case 'VGravur':
                    this.aktivite.takim.takimTipi = 'VKesici';
                    break;
                case 'NoktaGravur':
                    this.aktivite.takim.takimTipi = 'Elmas';
                    break;
                default:
                    this.aktivite.takim.takimTipi = 'VKesici';
            }
            this.cdr.markForCheck();
        }
    }

    // Edit methods
    editValue(field: string, label: string, unit: string): void {
        this.editingField = field;
        this.editLabel = label;
        this.editUnit = unit;

        // Get current value
        if (field.includes('.')) {
            const parts = field.split('.');
            this.editTempValue = this.aktivite[parts[0]]?.[parts[1]] || 0;
        } else {
            this.editTempValue = this.aktivite[field] || 0;
        }

        this.cdr.markForCheck();
        setTimeout(() => {
            const input = document.querySelector('.ep-input input') as HTMLInputElement;
            input?.focus();
            input?.select();
        }, 50);
    }

    applyEdit(): void {
        if (this.editingField && this.aktivite) {
            if (this.editingField.includes('.')) {
                const parts = this.editingField.split('.');
                if (this.aktivite[parts[0]]) {
                    this.aktivite[parts[0]][parts[1]] = this.editTempValue;
                }
            } else {
                this.aktivite[this.editingField] = this.editTempValue;
            }
        }
        this.editingField = null;
        this.cdr.markForCheck();
    }

    cancelEdit(): void {
        this.editingField = null;
        this.cdr.markForCheck();
    }

    // Cycle methods
    cycleOffset(): void {
        const offsets = ['Sol', 'Merkez', 'Sag'];
        const current = this.aktivite?.cizgiOffset || 'Merkez';
        const idx = offsets.indexOf(current);
        this.aktivite.cizgiOffset = offsets[(idx + 1) % offsets.length] as any;
        this.cdr.markForCheck();
    }

    cycleVAci(): void {
        const angles = [30, 45, 60, 90, 120];
        const current = this.aktivite?.vAci || 60;
        const idx = angles.indexOf(current);
        this.aktivite.vAci = angles[(idx + 1) % angles.length];
        this.cdr.markForCheck();
    }

    toggleOtomatikDerinlik(): void {
        this.aktivite.vOtomatikDerinlik = !this.aktivite.vOtomatikDerinlik;
        this.cdr.markForCheck();
    }

    cycleDolguDeseni(): void {
        const desens = ['Offset', 'Raster', 'CrossHatch', 'Spiral'];
        const current = this.aktivite?.dolguDeseni || 'Offset';
        const idx = desens.indexOf(current);
        this.aktivite.dolguDeseni = desens[(idx + 1) % desens.length] as any;
        this.cdr.markForCheck();
    }

    toggleDolguCevre(): void {
        this.aktivite.dolguCevre = !this.aktivite.dolguCevre;
        this.cdr.markForCheck();
    }

    cycleNoktaDesen(): void {
        const desens = ['Grid', 'Hex', 'Random'];
        const current = this.aktivite?.noktaDesen || 'Grid';
        const idx = desens.indexOf(current);
        this.aktivite.noktaDesen = desens[(idx + 1) % desens.length] as any;
        this.cdr.markForCheck();
    }

    cycleTakimTipi(): void {
        const tipler = ['VKesici', 'KarbonUc', 'Elmas', 'ParmakFreze', 'BallNose'];
        const current = this.aktivite?.takim?.takimTipi || 'VKesici';
        const idx = tipler.indexOf(current);
        this.aktivite.takim.takimTipi = tipler[(idx + 1) % tipler.length] as any;
        this.cdr.markForCheck();
    }

    editTakimAd(): void {
        const newAd = prompt('Takım Adı:', this.aktivite?.takim?.ad || 'T1');
        if (newAd) {
            this.aktivite.takim.ad = newAd;
            this.cdr.markForCheck();
        }
    }

    cycleGirisYontemi(): void {
        const yontemler = ['Dikey', 'Rampa', 'Yay'];
        const current = this.aktivite?.girisYontemi || 'Dikey';
        const idx = yontemler.indexOf(current);
        this.aktivite.girisYontemi = yontemler[(idx + 1) % yontemler.length] as any;
        this.cdr.markForCheck();
    }

    cycleCikisYontemi(): void {
        const yontemler = ['Dikey', 'Rampa', 'Yay'];
        const current = this.aktivite?.cikisYontemi || 'Dikey';
        const idx = yontemler.indexOf(current);
        this.aktivite.cikisYontemi = yontemler[(idx + 1) % yontemler.length] as any;
        this.cdr.markForCheck();
    }

    formatSure(dakika: number): string {
        if (!dakika || dakika <= 0) return '-';
        const dk = Math.floor(dakika);
        const sn = Math.round((dakika - dk) * 60);
        return dk > 0 ? `${dk}dk ${sn}sn` : `${sn}sn`;
    }

    tumunuTemizle(): void {
        if (this.aktivite) {
            this.aktivite.temizle();
            this.cdr.markForCheck();
        }
    }

    hesaplaVeKaydet(): void {
        if (this.aktivite && this.aktivite.profiller?.length > 0) {
            this.aktivite.takimYoluHesapla();
            this.onKaydet.emit(this.aktivite);
        }
    }

    iptal(): void {
        this.onIptal.emit();
    }
}
